import { useState } from 'react';
import { useSubscription, PLANS, PlanType } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
// import PlanCard from '../components/subscription/PlanCard';
import PaymentForm from '../components/subscription/PaymentForm';
import { Receipt, PieChart } from 'lucide-react';

const Billing = () => {
  const { user } = useAuth();
  const { currentPlan } = useSubscription();
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const handlePaymentSuccess = () => {
    setSelectedPlan(null);
  };

  const handlePaymentCancel = () => {
    setSelectedPlan(null);
  };

  // Mock payment history
  const paymentHistory = [
    {
      id: 'inv-001',
      date: '2023-07-01',
      amount: '$3.00',
      description: 'Standard Plan - Annual',
      status: 'Paid',
    },
    {
      id: 'inv-002',
      date: '2024-07-01',
      amount: '$3.00',
      description: 'Standard Plan - Annual (Upcoming)',
      status: 'Pending',
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Subscription & Billing
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your subscription plan and payment details.
          </p>
        </div>
      </div>

      {/* Current Plan */}
      <div className="mt-8 bg-white overflow-hidden shadow-sm rounded-lg divide-y divide-gray-200">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Current Subscription
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Your active subscription details.
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-md flex items-center justify-center">
                  <PieChart className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">{currentPlan.name} Plan</h4>
                  <p className="text-sm text-gray-500">
                    {currentPlan.price > 0
                      ? `$${currentPlan.price}/${currentPlan.billingPeriod}`
                      : 'Free'}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <dl className="divide-y divide-gray-200">
                  <div className="py-3 flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Domains</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {currentPlan.certificateLimit === Infinity
                        ? 'Unlimited'
                        : currentPlan.certificateLimit}
                    </dd>
                  </div>
                  <div className="py-3 flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Billing Cycle</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {currentPlan.price > 0 ? 'Annual' : 'N/A'}
                    </dd>
                  </div>
                  <div className="py-3 flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="text-sm font-medium">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-base font-medium text-gray-900 mb-3">
                  Change Subscription
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Select a new plan to upgrade or downgrade your subscription.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {/* Free Plan */}
                  <div
                    key="free"
                    className={`border rounded-md p-4 cursor-pointer transition-colors ${
                      currentPlan.id === 'free'
                        ? 'bg-primary-50 border-primary-300'
                        : 'bg-white border-gray-200 hover:border-primary-300'
                    }`}
                    // Optionally handle free plan switch here
                  >
                    <div className="font-medium">{PLANS.free.name}</div>
                    <div className="text-sm text-gray-500">Free</div>
                  </div>
                  {/* Standard Plan */}
                  <a
                    key="standard"
                    href={`https://buy.stripe.com/fZueVc4bI9qY5SSb3sdQQ00?prefilled_email=${encodeURIComponent(user?.email || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`border rounded-md p-4 transition-colors no-underline ${
                      currentPlan.id === 'standard'
                        ? 'bg-primary-50 border-primary-300 pointer-events-none'
                        : 'bg-white border-gray-200 hover:border-primary-300 cursor-pointer'
                    }`}
                  >
                    <div className="font-medium">{PLANS.standard.name}</div>
                    <div className="text-sm text-gray-500">${PLANS.standard.price}/year</div>
                  </a>
                  {/* Enterprise Plan */}
                  <a
                    key="enterprise"
                    href={`https://buy.stripe.com/6oUfZgdMibz62GG3B0dQQ01?prefilled_email=${encodeURIComponent(user?.email || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`border rounded-md p-4 transition-colors no-underline ${
                      currentPlan.id === 'enterprise'
                        ? 'bg-primary-50 border-primary-300 pointer-events-none'
                        : 'bg-white border-gray-200 hover:border-primary-300 cursor-pointer'
                    }`}
                  >
                    <div className="font-medium">{PLANS.enterprise.name}</div>
                    <div className="text-sm text-gray-500">${PLANS.enterprise.price}/year</div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="mt-8 bg-white overflow-hidden shadow-sm rounded-lg divide-y divide-gray-200">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Payment Links
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            We do not store your card details. Use the links below to manage your subscription payments securely via Stripe.
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6 flex flex-col gap-4">
          <a
            href={`https://buy.stripe.com/6oUfZgdMibz62GG3B0dQQ01?prefilled_email=${encodeURIComponent(user?.email || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 border border-primary-500 text-primary-700 bg-white rounded-md font-medium hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Pay for Standard Plan
          </a>
          <a
            href={`https://buy.stripe.com/fZueVc4bI9qY5SSb3sdQQ00?prefilled_email=${encodeURIComponent(user?.email || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 border border-primary-500 text-primary-700 bg-white rounded-md font-medium hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Pay for Enterprise Plan
          </a>
        </div>
      </div>

      {/* Billing History
      <div className="mt-8 bg-white overflow-hidden shadow-sm rounded-lg divide-y divide-gray-200">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Billing History
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              View your billing and payment history.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {showHistory ? 'Hide' : 'Show'}
          </button>
        </div>
        {showHistory && (
          <div className="px-4 py-5 sm:p-6">
            {paymentHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Date
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Amount
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="relative px-6 py-3"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paymentHistory.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {payment.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.amount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            payment.status === 'Paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <a href="#" className="text-primary-600 hover:text-primary-900">
                            {payment.status === 'Paid' ? 'View' : 'Pay Now'}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center">
                  <Receipt className="h-8 w-8 text-gray-400" />
                  <span className="ml-2 text-gray-500">No billing history available</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div> */}

      {/* Payment form modal */}
      {selectedPlan && (
        <div className="fixed inset-0 z-10 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={handlePaymentCancel}
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <PaymentForm
                      planId={selectedPlan}
                      onSuccess={handlePaymentSuccess}
                      onCancel={handlePaymentCancel}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;